import React from "react";
import styles from "../css/AttendanceStatisticsTable.module.css";
import { useTranslation } from "react-i18next";
import useFlexibleData from "../../../../hooks/useFlexibleData";

export default function AttendanceStatisticsTableByDate({
  rows,
  attendanceRecord,
  conditions,
  attendanceMode = "default",
  additionalAttendance,
}) {
  const {
    flexibleDataQuery: { data: flexibleData },
  } = useFlexibleData({
    schoolid: conditions?.schoolid,
    main_category: "schoolSetting",
  });

  const { t } = useTranslation();
  const isAdditionalMode = attendanceMode === "additional";
  const effectiveAdditionalAttendance =
    additionalAttendance || flexibleData?.[0]?.data_value?.additionalAttendance;
  const additionalOptions = Array.isArray(effectiveAdditionalAttendance?.options)
    ? effectiveAdditionalAttendance.options.filter(Boolean)
    : [];

  const calculateAdditionalAttendanceStats = (data, username) => {
    const filtered = data.filter((r) => {
      return r.username === username && !!r.additional_attendance;
    });

    const optionCounts = additionalOptions.reduce((acc, option) => {
      acc[option] = filtered.filter(
        (r) => r.additional_attendance === option
      ).length;
      return acc;
    }, {});

    return {
      totalRecords: filtered.length,
      optionCounts,
    };
  };

  const getDefaultAttendanceStatus = (record) =>
    record?.attendance_status || "";

  const calculateAttendanceStats = (data, username) => {
    const filtered = data.filter((r) => {
      return r.username === username;
    });

    if (filtered && filtered.length > 0) {
      const totalClassDays = filtered.filter(
        (r) => getDefaultAttendanceStatus(r) !== ""
      ).length;

      const attendedDays = filtered.filter(
        (r) => {
          const status = getDefaultAttendanceStatus(r);
          return (
            status !== "질결" &&
            status !== "무결" &&
            status !== "기결" &&
            status !== ""
          );
        }
      ).length;
      const sickAbsence = filtered.filter(
        (r) => getDefaultAttendanceStatus(r) === "질결"
      ).length;
      const justAbsence = filtered.filter(
        (r) => getDefaultAttendanceStatus(r) === "무결"
      ).length;
      const etcAbsence = filtered.filter(
        (r) => getDefaultAttendanceStatus(r) === "기결"
      ).length;
      const sickTardiness = filtered.filter(
        (r) => getDefaultAttendanceStatus(r) === "질지"
      ).length;
      const justTardiness = filtered.filter(
        (r) => getDefaultAttendanceStatus(r) === "무지"
      ).length;
      const etcTardiness = filtered.filter(
        (r) => getDefaultAttendanceStatus(r) === "기지"
      ).length;
      const sickLeave = filtered.filter(
        (r) => getDefaultAttendanceStatus(r) === "질조"
      ).length;
      const justLeave = filtered.filter(
        (r) => getDefaultAttendanceStatus(r) === "무조"
      ).length;
      const etcLeave = filtered.filter(
        (r) => getDefaultAttendanceStatus(r) === "기조"
      ).length;
      const sickNoclass = filtered.filter(
        (r) => getDefaultAttendanceStatus(r) === "질결과"
      ).length;
      const justNoclass = filtered.filter(
        (r) => getDefaultAttendanceStatus(r) === "무결과"
      ).length;
      const etcNoclass = filtered.filter(
        (r) => getDefaultAttendanceStatus(r) === "기결과"
      ).length;

      const result = {
        totalClassDays: totalClassDays,
        attendedDays: attendedDays,
        sickAbsence: sickAbsence,
        justAbsence: justAbsence,
        etcAbsence: etcAbsence,
        sickTardiness: sickTardiness,
        justTardiness: justTardiness,
        etcTardiness: etcTardiness,
        sickLeave: sickLeave,
        justLeave: justLeave,
        etcLeave: etcLeave,
        sickNoclass: sickNoclass,
        justNoclass: justNoclass,
        etcNoclass: etcNoclass,
      };

      return result;
    }
  };

  if (isAdditionalMode) {
    return (
      <div className="overflow-auto mb-4">
        <table
          className={`tableStyle table table-bordered table-sm text-center ${styles.tableStyle}`}
          style={{ fontSize: "13px" }}
        >
          <thead>
            <tr>
              <th style={{ minWidth: "60px", width: "150px" }}>
                {t("common.user.fullname") || "이름"}
              </th>
              {flexibleData &&
                flexibleData.length > 0 &&
                flexibleData[0].data_value?.attendanceOtherName && (
                  <th style={{ minWidth: "60px", width: "150px" }}>
                    {t("common.user.otherName") || "다른 이름"}
                  </th>
                )}
              <th style={{ width: "60px" }}>총 기록 수</th>
              {additionalOptions.map((option) => (
                <th key={option} style={{ width: "60px" }}>
                  {option}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows &&
              attendanceRecord &&
              rows.map((student, index) => {
                const stats = calculateAdditionalAttendanceStats(
                  attendanceRecord,
                  student.username
                );

                return (
                  <tr key={index}>
                    <td className="align-middle">{student.fullname}</td>
                    {flexibleData &&
                      flexibleData.length > 0 &&
                      flexibleData[0].data_value?.attendanceOtherName && (
                        <td className="align-middle">{student.otherName}</td>
                      )}
                    <td>{stats.totalRecords}</td>
                    {additionalOptions.map((option) => (
                      <td key={`${student.username}-${option}`}>
                        {stats.optionCounts[option] || 0}
                      </td>
                    ))}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="overflow-auto mb-4">
      <table
        className={`tableStyle table table-bordered table-sm text-center ${styles.tableStyle}`}
        style={{ fontSize: "13px" }}
      >
        <thead>
          <tr>
            <th style={{ minWidth: "60px", width: "150px" }} rowSpan="2">
              {t("common.user.fullname") || "이름"}
            </th>
            {flexibleData &&
              flexibleData.length > 0 &&
              flexibleData[0].data_value?.attendanceOtherName && (
                <th style={{ minWidth: "60px", width: "150px" }} rowSpan="2">
                  {t("common.user.otherName") || "다른 이름"}
                </th>
              )}
            <th rowSpan="2" style={{ width: "45px" }}>
              {t("teacher.attendanceStatistics.classDays") || "수업일수"}
            </th>
            <th rowSpan="2" style={{ width: "45px" }}>
              {t("teacher.attendanceStatistics.attendedDays") || "출석일수"}
            </th>
            <th colSpan="3" style={{ width: "135px" }}>
              {t("teacher.attendanceStatistics.absence") || "결석"}
            </th>
            <th colSpan="3" style={{ width: "135px" }}>
              {t("teacher.attendanceStatistics.tardiness") || "지각"}
            </th>
            <th colSpan="3" style={{ width: "135px" }}>
              {t("teacher.attendanceStatistics.earlyLeave") || "조퇴"}
            </th>
            <th colSpan="3" style={{ width: "135px" }}>
              {t("teacher.attendanceStatistics.noClass") || "결과"}
            </th>
          </tr>
          <tr>
            <td style={{ width: "45px" }}>
              {t("teacher.attendanceStatistics.sick") || "질병"}
            </td>
            <td
              className="p-0"
              style={{ letterSpacing: "-1px", width: "45px" }}
            >
              {t("teacher.attendanceStatistics.unexcused") || "미인정"}
            </td>
            <td style={{ width: "45px" }}>
              {t("teacher.attendanceStatistics.etc") || "기타"}
            </td>
            <td style={{ width: "45px" }}>
              {t("teacher.attendanceStatistics.sick") || "질병"}
            </td>
            <td
              className="p-0"
              style={{ letterSpacing: "-1px", width: "45px" }}
            >
              {t("teacher.attendanceStatistics.unexcused") || "미인정"}
            </td>
            <td style={{ width: "45px" }}>
              {t("teacher.attendanceStatistics.etc") || "기타"}
            </td>
            <td style={{ width: "45px" }}>
              {t("teacher.attendanceStatistics.sick") || "질병"}
            </td>
            <td
              className="p-0"
              style={{ letterSpacing: "-1px", width: "45px" }}
            >
              {t("teacher.attendanceStatistics.unexcused") || "미인정"}
            </td>
            <td style={{ width: "45px" }}>
              {t("teacher.attendanceStatistics.etc") || "기타"}
            </td>
            <td style={{ width: "45px" }}>
              {t("teacher.attendanceStatistics.sick") || "질병"}
            </td>
            <td
              className="p-0"
              style={{ letterSpacing: "-1px", width: "45px" }}
            >
              {t("teacher.attendanceStatistics.unexcused") || "미인정"}
            </td>
            <td style={{ width: "45px" }}>
              {t("teacher.attendanceStatistics.etc") || "기타"}
            </td>
          </tr>
        </thead>
        <tbody>
          {rows &&
            attendanceRecord &&
            rows.map((student, index) => {
              const stats = calculateAttendanceStats(
                attendanceRecord,
                student.username
              );
              return (
                <tr key={index}>
                  <td className="align-middle">{student.fullname}</td>
                  {flexibleData &&
                    flexibleData.length > 0 &&
                    flexibleData[0].data_value?.attendanceOtherName && (
                      <td className="align-middle">{student.otherName}</td>
                    )}
                  <td>{stats?.totalClassDays}</td>
                  <td>{stats?.attendedDays}</td>
                  <td>{stats?.sickAbsence}</td>
                  <td>{stats?.justAbsence}</td>
                  <td>{stats?.etcAbsence}</td>
                  <td>{stats?.sickTardiness}</td>
                  <td>{stats?.justTardiness}</td>
                  <td>{stats?.etcTardiness}</td>
                  <td>{stats?.sickLeave}</td>
                  <td>{stats?.justLeave}</td>
                  <td>{stats?.etcLeave}</td>
                  <td>{stats?.sickNoclass}</td>
                  <td>{stats?.justNoclass}</td>
                  <td>{stats?.etcNoclass}</td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}
